import React from "react"
import { Badge, Table } from "reactstrap"
import { useTranslation } from "react-i18next"
import { formatCurrencyAmount } from "utils/currency"
import TableActionsDropdown from "components/Shared/TableActionsDropdown"
import formatDateTime from "components/Shared/formatDateTime"
import LoadingSpinner from "./LoadingSpinner"
import EmptyState from "./EmptyState"
import { getPartnerBranchName, getPartnerProfilePhoto } from "./branchUtils"
import { getRiderAvailabilityDisplay } from "utils/riderAvailability"

const statusColor = status => {
  switch ((status || "").toLowerCase()) {
    case "approved":
      return "success"
    case "pending":
      return "warning"
    case "suspended":
      return "danger"
    case "rejected":
      return "secondary"
    default:
      return "light"
  }
}

const SortIcon = ({ field, sortBy, sortOrder }) => {
  if (sortBy !== field) return <i className="bx bx-sort ms-1 text-muted"></i>
  return sortOrder === "asc" ? (
    <i className="bx bx-sort-up ms-1 text-primary"></i>
  ) : (
    <i className="bx bx-sort-down ms-1 text-primary"></i>
  )
}

const translateStatus = (status, t) => {
  if (!status) return t("Unknown")
  const key = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
  return t(key, { defaultValue: status })
}

const DeliveryPartnersList = ({
  partners = [],
  loading,
  permissions = {},
  onViewDetails,
  onDailySummary,
  onVerifyDocuments,
  onUpdateStatus,
  onEditPartner,
  onSort,
  sortBy,
  sortOrder,
}) => {
  const { t, i18n } = useTranslation()
  const canAddUpdate = Boolean(permissions.addUpdate)
  const canVerifyDocuments =
    Boolean(permissions.documents) && Boolean(permissions.canViewDocumentsTab)
  const canViewEarningsColumn = Boolean(permissions.canViewEarningsTab)

  const handleSort = field => {
    const newOrder = sortBy === field && sortOrder === "asc" ? "desc" : "asc"
    onSort(field, newOrder)
  }

  const getPartnerActionItems = partner => {
    const items = [
      {
        key: "view",
        content: (
          <>
            <i className="bx bx-show"></i>
            {t("View Details")}
          </>
        ),
        onClick: () => onViewDetails(partner),
      },
      {
        key: "daily-summary",
        content: (
          <>
            <i className="bx bx-calendar"></i>
            {t("Daily Summary")}
          </>
        ),
        onClick: () => onDailySummary?.(partner),
      },
    ]

    if (canAddUpdate) {
      items.push({
        key: "edit",
        content: (
          <>
            <i className="bx bx-edit-alt"></i>
            {t("Edit Rider")}
          </>
        ),
        onClick: () => onEditPartner(partner),
      })
    }

    if (canVerifyDocuments) {
      items.push({
        key: "verify",
        content: (
          <>
            <i className="bx bx-file-find"></i>
            {t("Verify Documents")}
          </>
        ),
        onClick: () => onVerifyDocuments(partner),
      })
    }

    if (canAddUpdate && (partner.status || "").toLowerCase() !== "suspended") {
      items.push({
        key: "suspend",
        content: (
          <>
            <i className="bx bx-block text-warning"></i>
            {t("Suspend")}
          </>
        ),
        onClick: () => onUpdateStatus(partner, "Suspended"),
      })
    }

    if (canAddUpdate && (partner.status || "").toLowerCase() === "suspended") {
      items.push({
        key: "reactivate",
        content: (
          <>
            <i className="bx bx-check-circle text-success"></i>
            {t("Reactivate")}
          </>
        ),
        onClick: () => onUpdateStatus(partner, "Approved"),
      })
    }

    return items
  }

  return (
    <div>
      {loading ? (
        <LoadingSpinner text={t("Loading riders...")} />
      ) : partners.length === 0 ? (
        <EmptyState
          title={t("No riders found")}
          subtitle={t("Try adjusting your filters or search query")}
        />
      ) : (
        <div className="table-responsive">
          <Table className="table align-middle table-nowrap mb-0">
            <thead className="table-light">
              <tr>
                <th>{t("S. No.")}</th>
                <th>{t("Photo")}</th>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => handleSort("name")}
                >
                  {t("Name")} <SortIcon field="name" sortBy={sortBy} sortOrder={sortOrder} />
                </th>
                <th>{t("Phone")}</th>
                <th>{t("Branch")}</th>
                <th>{t("Status")}</th>
                <th>{t("Availability")}</th>
                <th>{t("Deliveries")}</th>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => handleSort("rating")}
                >
                  {t("Rating")}{" "}
                  <SortIcon field="rating" sortBy={sortBy} sortOrder={sortOrder} />
                </th>
                {canViewEarningsColumn ? (
                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSort("earnings")}
                  >
                    {t("Earnings")}{" "}
                    <SortIcon field="earnings" sortBy={sortBy} sortOrder={sortOrder} />
                  </th>
                ) : null}
                <th>{t("Last Active")}</th>
                <th style={{ minWidth: "fit-content", whiteSpace: "nowrap" }}>{t("Actions")}</th>
              </tr>
            </thead>
            <tbody>
              {partners.map((partner, idx) => {
                const profilePhoto = getPartnerProfilePhoto(partner)
                return (
                <tr key={partner._id || partner.id || idx}>
                  <td>{idx + 1}</td>
                  <td>
                    <div
                      className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                      style={{ width: 40, height: 40, overflow: "hidden" }}
                    >
                      {profilePhoto ? (
                        <img
                          src={profilePhoto}
                          alt={partner.name}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        <i className="bx bx-user text-muted"></i>
                      )}
                    </div>
                  </td>
                  <td className="fw-semibold">{partner.name || t("N/A")}</td>
                  <td>
                    {partner.phone ? (
                      <span
                        dir="ltr"
                        style={{ unicodeBidi: "bidi-override" }}
                      >
                        {partner.phone}
                      </span>
                    ) : (
                      t("N/A")
                    )}
                  </td>
                  <td>{getPartnerBranchName(partner, i18n.language) || "-"}</td>
                  <td>
                    <Badge color={statusColor(partner.status)}>
                      {translateStatus(partner.status, t)}
                    </Badge>
                  </td>
                  <td>
                    {(() => {
                      const availability = getRiderAvailabilityDisplay(partner, t)
                      return (
                        <span className="d-inline-flex align-items-center gap-1">
                          <span
                            className="rounded-circle flex-shrink-0"
                            style={{
                              width: 8,
                              height: 8,
                              backgroundColor: availability.color,
                            }}
                          />
                          <span className={availability.textClass}>
                            {availability.label}
                          </span>
                        </span>
                      )
                    })()}
                  </td>
                  <td>{partner.totalDeliveries ?? 0}</td>
                  <td>
                    <span>
                      {Number(partner.averageRating ?? 0).toFixed(1)}{" "}
                      <i className="bx bxs-star text-warning"></i>
                    </span>
                  </td>
                  {canViewEarningsColumn ? (
                    <td>{formatCurrencyAmount(partner.totalEarnings ?? 0)}</td>
                  ) : null}
                  <td>
                    {partner.lastActive
                      ? formatDateTime(partner.lastActive)
                      : "-"}
                  </td>
                  <td>
                    <TableActionsDropdown items={getPartnerActionItems(partner)} />
                  </td>
                </tr>
              )})}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  )
}

export default DeliveryPartnersList
